<template>
  <div class="custom-ui-autocomplete-wrapper" v-click-outside="close">
    <div class="custom-ui-autocomplete">
      <label :for="inputId" class="custom-ui-autocomplete-label" v-if="label" v-text="label" />
      <div class="custom-ui-autocomplete-input" :class="styles">
        <input
          :id="inputId"
          name="ui-autocomplete"
          type="text"
          v-model="model"
          @input="showDropdown"
          @focus="showDropdown"
          @blur="close"
          :placeholder="placeholder"
        />
        <span class="custom-ui-autocomplete-input-icon">
          <i class="spin-icon fa-solid fa-spinner" v-if="loading" />
          <i class="fa-solid fa-magnifying-glass" v-else />
        </span>
      </div>
      <div class="custom-ui-autocomplete-list" v-if="display" @click.stop>
        <div v-if="!isSlot && !loading">
          <div
            class="custom-ui-autocomplete-list-item"
            v-for="(item, index) in items"
            :key="index"
            @click="onSelect(item)"
            v-text="item[field ?? '']"
          />
        </div>
        <div v-if="isSlot && !loading">
          <slot />
        </div>
        <div v-if="loading" class="custom-ui-autocomplete-list-loading">
          <i class="spin-icon fa-solid fa-spinner" />
        </div>
        <div
          v-if="!loading && items.length === 0"
          class="custom-ui-autocomplete-list-noresult"
          v-text="t('admin_global_noresults')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { IAutoComplete } from '@/types/Components'
  import { UseI18n } from '@/utils-ts'
  import { computed, ref, useId } from 'vue'
  const { t } = UseI18n()

  const inputId = useId()

  const {
    label,
    placeholder,
    items,
    loading = false,
    field,
    size = 'sm',
    isSlot = false,
  } = defineProps<IAutoComplete>()
  const model = defineModel<string>('modelValue')
  const emit = defineEmits<{
    (e: 'select', item: any): void
    (e: 'type', value: string): void
  }>()

  const display = ref<boolean>(false)

  const styles = computed(() => [`size-${size}`])
  const showDropdown = () => {
    emit('type', model.value ?? '')
    display.value = true
  }

  const close = () => {
    setTimeout(() => {
      display.value = false
    }, 200)
  }

  const onSelect = (item: any) => {
    model.value = item[field ?? '']
    emit('select', item)
  }
</script>

<style lang="scss" scoped>
  .custom-ui-autocomplete-wrapper {
    width: 100%;
    .custom-ui-autocomplete {
      position: relative;
      .custom-ui-autocomplete-label {
        @include column-label;
      }
      .custom-ui-autocomplete-input {
        width: 100%;
        display: flex;
        border: 1px solid $border-color;
        justify-content: space-between;
        align-items: center;
        border-radius: 5px;
        padding: calc(0.5em - 1.5px) 1em;
        transition: 0.2s all ease;

        .custom-ui-autocomplete-input-icon {
          color: $primary-text-color;
        }
        input {
          width: 100%;
          border: none;
          height: 100%;
          border-radius: 4px;
          outline: none;
          font-size: 14px;
          color: #000;
          transition: all 0.3s ease;
          &:disabled {
            cursor: not-allowed;
            background-color: transparent;
          }
        }
      }
      .custom-ui-autocomplete-list {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        max-height: 200px;
        overflow-y: auto;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
        border-top: none;
        border-radius: 0 0 4px 4px;
        background: #fff;
        z-index: 999;
        .custom-ui-autocomplete-list-item {
          padding: 10px 10px;
          font-size: 14px;
          color: #000;
          cursor: pointer;
          transition: 0.2s all ease;
          &:hover {
            background: #e0e0e0;
          }
        }
        .custom-ui-autocomplete-list-loading {
          padding: 2rem 0;
          @include flexible($justify: center);
        }
        .custom-ui-autocomplete-list-noresult {
          padding: 2rem 0;
          @include flexible($justify: center);
        }
      }
    }
    .spin-icon {
      animation: spin 0.75s linear infinite;
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
</style>
